/** Compact elapsed time, e.g. "12s", "7m", "2h 3m", "3d 4h". */
export const formatDuration = (seconds: number): string => {
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  if (total < 3600) return `${Math.floor(total / 60)}m`;
  if (total < 86_400) {
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  return hours ? `${days}d ${hours}h` : `${days}d`;
};

/**
 * How long after surfacing a view was first served to anyone. `null` means the
 * system has no record of serving it — the report's headline finding, not missing
 * data.
 *
 * Wording claims delivery only, never reading: the backend records that the
 * payload was served, not that a human looked at it (BE-2151). "Shown", not
 * "seen".
 *
 * A negative latency is not an error: someone can be served a view before the
 * agent surfaces it, by opening it from a list that is curated afterwards. It
 * reads as an ordering, never as a negative duration.
 */
export const formatFirstSeen = (seconds: number | null): string => {
  if (seconds === null) return "no record of being shown";
  if (seconds < 0) return "shown before it was surfaced";
  return `first shown after ${formatDuration(seconds)}`;
};

/**
 * One viewer's latency, beside their name, timed against the surfacing whose
 * audience they matched. Carries the preposition so a bare duration can't be
 * misread as time spent reading.
 *
 * Null means they matched no audience at all — they opened the view themselves, so
 * it was never put in front of them and there is no latency to state. Saying
 * nothing there would read as a missing value; this names it.
 */
export const formatViewerLatency = (seconds: number | null): string => {
  if (seconds === null) return "opened it directly";
  return seconds < 0 ? "before surfacing" : `after ${formatDuration(seconds)}`;
};
